import { AgGridButtons } from "@/controls/buttons/AgGridButtons";
import { CasterScrapReason } from "@/pages/caster/models/Catalogs";
import { ProcessData } from "@/pages/caster/models/ProcessData";
import {
	ColDef,
	ColGroupDef,
	GridOptions,
	ICellRendererParams,
} from "ag-grid-community";
import { useProductionTableStyles } from "../../production-table/styles/useProductionTableStyles";

export interface IScrapRow {
	id: string; // 'scrap1' or 'scrap2'
	length: number | null;
	pieces: number | null;
	codeId: number | null;
}

interface UseScrapTableDataProps {
	processData: ProcessData | null;
	scrapReasons: CasterScrapReason[];
	editCallback: (scrap: IScrapRow) => void;
}

const formatLength = (lengthInInches: number | null | undefined): string => {
	if (lengthInInches === null || lengthInInches === undefined)
		lengthInInches = 0;
	const feet = Math.floor(lengthInInches / 12);
	const inches = lengthInInches % 12;
	return `${feet} ft ${inches} in`;
};

export const useScrapTableData = ({
	processData,
	scrapReasons,
	editCallback,
}: UseScrapTableDataProps) => {
	const scrapReasonsMap = new Map(
		scrapReasons.map((reason) => [
			reason.ScrapReasonId,
			reason.ScrapReasonName,
		]),
	);

	const classes = useProductionTableStyles();
	const rowData: IScrapRow[] = [];

	if (processData) {
		rowData.push({
			id: "scrap1",
			length: processData.Scrap1Length,
			pieces: processData.Scrap1Pieces,
			codeId: processData.Scrap1CodeId,
		});
		rowData.push({
			id: "scrap2",
			length: processData.Scrap2Length,
			pieces: processData.Scrap2Pieces,
			codeId: processData.Scrap2CodeId,
		});
	}

	const columns: ColGroupDef[] = [
		{
			headerName: "Scrap",
			children: [
				{
					field: "length",
					headerName: "Length",
					flex: 1,
					minWidth: 70,
					valueFormatter: (params) => formatLength(params.value),
				},
				{
					field: "pieces",
					headerName: "Pieces",
					flex: 1,
					minWidth: 60,
				},
				{
					field: "codeId",
					headerName: "Code",
					flex: 1,
					minWidth: 108,
					valueGetter: (params) => {
						const codeId = params.data.codeId;
						return codeId ? scrapReasonsMap.get(codeId) || "" : "";
					},
				},
				{
					field: "actions",
					headerName: "",
					sortable: false,
					filter: false,
					minWidth: 60,
					maxWidth: 60,
					cellRenderer: (params: ICellRendererParams<IScrapRow>) => {
						const rowData = params.data;
						return rowData ? (
							<AgGridButtons editItem={() => editCallback(rowData)} />
						) : null;
					},
				},
			],
		},
	];

	const defaultColDef: ColDef = {
		filter: false,
		floatingFilter: false,
		sortable: false,
		resizable: false,
		editable: false,
		suppressHeaderMenuButton: true,
	};

	const statusBar: GridOptions["statusBar"] = {
		statusPanels: [
			{
				statusPanel: () => (
					<div
						style={{
							textAlign: "center",
							width: "100%",
						}}
					>
						Scrap Tons{" "}
						{processData?.ScrapTons !== null &&
						processData?.ScrapTons !== undefined
							? processData.ScrapTons.toFixed(1)
							: ""}
					</div>
				),
				align: "center",
			},
		],
	};

	return { columns, defaultColDef, rowData, statusBar };
};
